/**
 * MCP tool: graph_outline
 *
 * Returns a compressed outline of a file (signatures, decorators, docstrings).
 * Resolution order:
 *   1. In-memory cache
 *   2. Pre-computed .outline.json (generated by CLI `outline` command)
 *   3. On-the-fly generation via outline module (tree-sitter or regex)
 */
import type { Database } from "../../core/db.js";
import { existsSync, readFileSync } from "node:fs";
import { join, resolve } from "node:path";
import { OutlineCache } from "../../outline/cache.js";
import { generateOutline, formatOutlineMarkdown, formatOutlineJson } from "../../outline/index.js";
import type { FileOutline } from "../../shared/types.js";

const outlineCache = new OutlineCache();

export function handleOutline(_db: Database, graphDir: string, args: Record<string, unknown>) {
  const filePath = args.file_path as string;
  const format = (args.format as string) ?? "markdown";
  if (!filePath) return { content: [{ type: "text" as const, text: "Error: 'file_path' is required" }], isError: true };

  // 1. Cache hit
  const cached = outlineCache.get(filePath);
  if (cached) {
    const text = format === "json" ? formatOutlineJson(cached) : formatOutlineMarkdown(cached);
    return { content: [{ type: "text" as const, text }] };
  }

  // 2. Pre-computed outline
  const preComputed = join(graphDir, "outlines", filePath + ".outline.json");
  if (existsSync(preComputed)) {
    try {
      const outline = JSON.parse(readFileSync(preComputed, "utf-8")) as FileOutline;
      outlineCache.set(filePath, outline);
      const text = format === "json" ? formatOutlineJson(outline) : formatOutlineMarkdown(outline);
      return { content: [{ type: "text" as const, text }] };
    } catch { /* fall through to on-the-fly */ }
  }

  // 3. On-the-fly generation
  const workspaceRoot = resolve(graphDir, "..");
  const absolutePath = resolve(workspaceRoot, filePath);
  if (!existsSync(absolutePath)) {
    return { content: [{ type: "text" as const, text: `File not found: ${filePath}` }] };
  }

  // generateOutline is async — wrap in a sync-compatible pattern for MCP handler
  // Since MCP tool handlers can return promises, we use an async IIFE
  return (async () => {
    const source = readFileSync(absolutePath, "utf-8");
    const outline = await generateOutline(filePath, source);
    if (!outline) {
      return { content: [{ type: "text" as const, text: `Unsupported language for outline: ${filePath}` }] };
    }
    outlineCache.set(filePath, outline);
    const text = format === "json" ? formatOutlineJson(outline) : formatOutlineMarkdown(outline);
    return { content: [{ type: "text" as const, text }] };
  })();
}
